import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard,
  Truck,
  Users,
  Wrench,
  Fuel,
  MapPin,
  Settings,
  Menu,
  X,
  LogOut,
  User,
  Shield,
  ChevronDown,
  ClipboardList,
  Navigation,
  Bell,
  DollarSign,
  FileText
} from "lucide-react";

const navItems = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/vehicles", icon: Truck, label: "Vehicles" },
  { path: "/drivers", icon: Users, label: "Drivers" },
  { path: "/alerts", icon: Bell, label: "Alerts" },
  { path: "/budget", icon: DollarSign, label: "Budget" },
  { path: "/reports", icon: FileText, label: "Reports" },
  { path: "/repair-orders", icon: ClipboardList, label: "Repair Orders" },
  { path: "/service-history", icon: Wrench, label: "Service History" },
  { path: "/fuel-records", icon: Fuel, label: "Fuel Records" },
  { path: "/mission-planner", icon: Navigation, label: "Mission Planner" },
  { path: "/live-tracking", icon: MapPin, label: "Live Tracking" },
  { path: "/settings", icon: Settings, label: "Settings" },
];

export const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin: { label: "ADMIN", className: "bg-primary/20 text-primary" },
      fleet_manager: { label: "FLEET MGR", className: "bg-blue-500/20 text-blue-400" },
      driver: { label: "DRIVER", className: "bg-yellow-500/20 text-yellow-400" }
    };
    return badges[role] || badges.driver;
  };

  return (
    <div className="min-h-screen bg-background tactical-grid" data-testid="dashboard-layout">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-card border-r border-border transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-primary/10 border border-primary/30 flex items-center justify-center">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="font-heading text-sm font-bold tracking-tight text-foreground">
                  FLEET COMMAND
                </h1>
                <p className="text-[10px] font-mono text-muted-foreground">TDFC SYSTEM</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-sm font-heading text-sm tracking-wide transition-colors ${
                      isActive
                        ? "bg-primary/10 text-primary border-l-2 border-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent"
                    }`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label.toUpperCase()}
                </NavLink>
              ))}
            </nav>
          </ScrollArea>

          {/* User Section */}
          <div className="p-4 border-t border-border">
            <p className="text-[9px] font-mono text-muted-foreground/50 text-center mb-2">Powered by Akendra Pty Ltd</p>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="w-full justify-between px-3 py-2 h-auto hover:bg-accent rounded-sm"
                  data-testid="user-menu-trigger"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-sm bg-primary/10 border border-primary/30 flex items-center justify-center">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                    <div className="text-left">
                      <p className="text-xs font-medium text-foreground truncate max-w-[120px]">
                        {user?.full_name}
                      </p>
                      <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${getRoleBadge(user?.role).className}`}>
                        {getRoleBadge(user?.role).label}
                      </span>
                    </div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-card border-border rounded-sm">
                <DropdownMenuItem className="font-mono text-xs text-muted-foreground">
                  {user?.email}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-border" />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="text-destructive focus:text-destructive cursor-pointer"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-card/80 backdrop-blur-md border-b border-border">
          <div className="flex items-center justify-between px-4 py-3">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </Button>
            
            <div className="flex items-center gap-4 ml-auto">
              <div className="hidden sm:flex items-center gap-2 text-xs font-mono text-muted-foreground">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                SYSTEM ONLINE
              </div>
              <div className="text-xs font-mono text-muted-foreground">
                {new Date().toLocaleDateString("en-TZ", {
                  weekday: "short",
                  year: "numeric",
                  month: "short",
                  day: "numeric"
                })}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6" data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
